<template>
  <app-collapse class="mt-1">
    <app-collapse-item
      :is-visible="false"
      class="collapsible-section-bg"
      title="Additions and deductions, costs subject to VAT"
    >
      <template #header>
        <span
          class="lead collapse-title"
        >
          <section-status-icon
            :has-data="hasAdditionsDeductionsData"
            :has-errors="hasSectionErrors"
          />
          Additions and deductions, costs subject to VAT
        </span>
      </template>
      <!-- Method of dividing additions and deductions field -->
      <b-row v-if="parcel.costs_distribution_data">
        <b-col
          cols="12"
          md="3"
        >
          <validation-provider
            v-slot="{ errors }"
            name="Method of dividing additions and deductions"
          >
            <b-form-group>
              <label>
                Method of dividing additions and deductions
                <small class="text-muted">140400000C</small>
                <feather-icon
                  v-b-tooltip.hover.top="'Method used to divide additions and deductions across items'"
                  icon="InfoIcon"
                  size="14"
                  class="ml-50 text-muted"
                />
              </label>
              <v-select
                v-model="parcel.costs_distribution_data.costs_distribution_method"
                :options="methodOptions"
                :reduce="option => option.value"
                :disabled="disabled"
                :state="errors.length > 0 ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-col>
      </b-row>

      <div
        v-for="(deduction, deductionIndex) in parcel.costs_distribution_data && parcel.costs_distribution_data.additions_deductions"
        :key="deductionIndex"
        class="border rounded pl-3 pr-3 pt-1 mb-2"
      >
        <b-row>
          <b-col
            cols="12"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Code"
              :rules="rowHasData(deduction) ? 'required' : ''"
            >
              <b-form-group>
                <label>
                  Code <span class="text-danger">*</span>
                  <small class="text-muted">1404008000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Code for addition or deduction'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <v-select
                  v-model="deduction.code"
                  :options="codeOptions"
                  :reduce="option => option.value"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Amount"
              :rules="rowHasData(deduction) ? 'required' : ''"
            >
              <b-form-group>
                <label>
                  Amount <span class="text-danger">*</span>
                  <small class="text-muted">1404014000</small>
                </label>
                <b-form-input
                  v-model="deduction.amount"
                  v-b-tooltip.hover.top="getIssueMessage(`costs_distribution_data.additions_deductions.${deductionIndex}.amount`)"
                  :class="getIssueClass(`costs_distribution_data.additions_deductions.${deductionIndex}.amount`)"
                  :disabled="disabled"
                  type="number"
                  step="0.01"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="2"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Currency"
              :rules="rowHasData(deduction) ? 'required' : ''"
            >
              <b-form-group>
                <label>
                  Currency <span class="text-danger">*</span>
                  <small class="text-muted">140400000B</small>
                </label>
                <v-select
                  v-model="deduction.currency"
                  :options="currencyOptions"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="2"
            class="text-right"
          >
            <b-button
              v-if="!disabled"
              variant="link"
              size="sm"
              class="text-danger mt-2"
              @click="removeDeduction(deductionIndex)"
            >
              <feather-icon icon="TrashIcon" />
              Delete
            </b-button>
          </b-col>
        </b-row>
      </div>

      <b-row>
        <b-col class="text-right">
          <b-button
            v-if="!disabled"
            variant="info"
            size="sm"
            @click="addDeduction"
          >
            <feather-icon icon="PlusIcon" />
            New row
          </b-button>
        </b-col>
      </b-row>
    </app-collapse-item>
  </app-collapse>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BButton,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationProvider } from 'vee-validate'
import vSelect from 'vue-select'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import errorHighlightingMixin from '@/mixins/errorHighlightingMixin'
import SectionStatusIcon from '@/views/shipments/declarations/modals/components/h1/SectionStatusIcon.vue'

export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BButton,
    ValidationProvider,
    vSelect,
    AppCollapse,
    AppCollapseItem,
    SectionStatusIcon,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [errorHighlightingMixin],
  props: {
    parcel: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      codeOptions: [
        { label: 'test', value: '1' },
      ],
      currencyOptions: [
        'EUR', 'USD', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'CNY', 'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'HUF', 'RON', 'BGN', 'HRK', 'RUB', 'TRY', 'INR', 'BRL', 'ZAR', 'KRW', 'MXN', 'IDR', 'MYR', 'PHP', 'THB', 'SGD', 'NZD', 'HKD', 'AED', 'SAR', 'ILS', 'EGP',
      ],
      methodOptions: [
        { label: 'Proportional to value', value: 1 },
        { label: 'Proportional to quantity', value: 2 },
        { label: 'Proportional to weight', value: 3 },
        { label: 'Equal distribution', value: 4 },
      ],
    }
  },
  computed: {
    hasAdditionsDeductionsData() {
      const hasMethodData = this.parcel && this.parcel.costs_distribution_data && this.parcel.costs_distribution_data.costs_distribution_method
      const hasDeductionsData = this.parcel && this.parcel.costs_distribution_data && Array.isArray(this.parcel.costs_distribution_data.additions_deductions) && this.parcel.costs_distribution_data.additions_deductions.some(deduction => deduction.code || deduction.amount || deduction.currency)
      return !!(hasMethodData || hasDeductionsData)
    },
    hasSectionErrors() {
      return Object.keys(this.issuesMap).some(key => key.startsWith('costs_distribution_data.'))
    },
  },
  methods: {
    addDeduction() {
      // Ensure costs_distribution_data and its additions_deductions array exist
      if (!this.parcel.costs_distribution_data) {
        this.$set(this.parcel, 'costs_distribution_data', { costs_distribution_method: null, additions_deductions: [] })
      } else if (!this.parcel.costs_distribution_data.additions_deductions) {
        this.$set(this.parcel.costs_distribution_data, 'additions_deductions', [])
      }
      this.parcel.costs_distribution_data.additions_deductions.push({
        code: null,
        amount: '',
        currency: null,
      })
    },
    removeDeduction(deductionIndex) {
      this.$bvModal.msgBoxConfirm('Are you sure you want to delete this row?', {
        title: 'Please Confirm',
        size: 'sm',
        okVariant: 'danger',
        okTitle: 'Yes',
        cancelTitle: 'No',
        centered: true,
      }).then(value => {
        if (value) {
          this.parcel.costs_distribution_data.additions_deductions.splice(deductionIndex, 1)
        }
      })
    },
    rowHasData(deduction) {
      return !!(deduction.code || deduction.amount || deduction.currency)
    },
  },
}
</script>

<style scoped lang="scss">
@import '@core/scss/base/bootstrap-extended/variables';
@import '@core/scss/base/core/mixins/declarations-mixins';

.collapsible-section-bg {
  @include collapsible-section-bg;
}

.input-warning {
  @include input-warning;
}

.input-danger {
  @include input-danger;
}
</style>
